import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowDownUp,
  Ban,
  Loader2,
  Monitor,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useBusiness } from '@/features/auth/auth.store';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCreatePosLane,
  useDeletePosLane,
  usePosLanes,
  useUpdatePosLane,
  type PosLane,
} from './pos-lanes.api';

/**
 * The tills in this store.
 *
 * The question a manager actually opens this page with is "which lane is
 * wrong" — blocked, or running an old build, or silent since yesterday. So the
 * page leads with those three counts and lets them be used as filters, rather
 * than making somebody read a version column row by row.
 */

type Filter = 'all' | 'blocked' | 'outdated';
type Sort = 'tag' | 'version' | 'lastTransaction';

export function PosLanesPage() {
  const { t } = useTranslation();
  const business = useBusiness();
  const lanes = usePosLanes(business?.id ?? null);

  const [term, setTerm] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('tag');
  const [editing, setEditing] = useState<PosLane | 'new' | null>(null);

  const latestVersion = lanes.data?.latestVersion ?? null;
  const all = lanes.data?.lanes ?? [];
  // `isLoading`, not `isPending`: the list waits on a store being picked.
  const loading = lanes.isLoading;
  const busy = lanes.isFetching && !loading;

  const isOutdated = (lane: PosLane) =>
    !!latestVersion
    && !!lane.lastReportedVersion
    && lane.lastReportedVersion !== latestVersion;

  const counts = {
    all: all.length,
    blocked: all.filter((lane) => lane.isBlocked).length,
    outdated: all.filter(isOutdated).length,
  };

  const search = term.trim().toLowerCase();
  const rows = all
    .filter((lane) =>
      filter === 'blocked'
        ? lane.isBlocked
        : filter === 'outdated'
          ? isOutdated(lane)
          : true
    )
    .filter((lane) =>
      search === ''
        ? true
        : [
            lane.tag,
            lane.businessName,
            lane.lastReportedVersion,
            lane.lastReportedDeviceManufacturer,
            lane.lastReportedDeviceModel,
            lane.lastReportedDeviceName,
            lane.lastReportedTerminalId,
          ]
            .join(' ')
            .toLowerCase()
            .includes(search)
    )
    .sort((a, b) => {
      if (sort === 'version')
        return (b.lastReportedVersion ?? '').localeCompare(
          a.lastReportedVersion ?? ''
        );
      if (sort === 'lastTransaction')
        return (
          new Date(b.lastTransactionOn ?? 0).getTime()
          - new Date(a.lastTransactionOn ?? 0).getTime()
        );
      return (a.tag ?? '').localeCompare(b.tag ?? '');
    });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('posLanes.title')}
        description={business?.name ?? t('posLanes.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={lanes.isFetching}
              onClick={() => void lanes.refetch()}
            >
              <RefreshCw className={lanes.isFetching ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{t('posLanes.refresh')}</span>
            </Button>
            <Button
              size="sm"
              disabled={!business}
              onClick={() => setEditing('new')}
            >
              <Plus />
              <span className="hidden sm:inline">{t('posLanes.newLane')}</span>
            </Button>
          </>
        }
      />

      {/* The three readings of "which lane is wrong", doubling as filters. */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Tile
          label={t('posLanes.allLanes')}
          value={counts.all}
          active={filter === 'all'}
          loading={loading}
          busy={busy}
          onClick={() => setFilter('all')}
        />
        <Tile
          label={t('posLanes.blocked')}
          value={counts.blocked}
          tone={counts.blocked > 0 ? 'warn' : undefined}
          active={filter === 'blocked'}
          loading={loading}
          busy={busy}
          onClick={() => setFilter(filter === 'blocked' ? 'all' : 'blocked')}
        />
        <Tile
          label={t('posLanes.outdated')}
          value={counts.outdated}
          hint={
            latestVersion
              ? t('posLanes.latestVersion', { version: latestVersion })
              : undefined
          }
          tone={counts.outdated > 0 ? 'warn' : undefined}
          active={filter === 'outdated'}
          loading={loading}
          busy={busy}
          onClick={() => setFilter(filter === 'outdated' ? 'all' : 'outdated')}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={term}
            className="h-10 pl-9"
            placeholder={t('posLanes.searchPlaceholder')}
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
        <Select value={sort} onValueChange={(value) => setSort(value as Sort)}>
          <SelectTrigger className="h-10 sm:w-56">
            <ArrowDownUp className="size-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tag">{t('posLanes.sortTag')}</SelectItem>
            <SelectItem value="version">{t('posLanes.sortVersion')}</SelectItem>
            <SelectItem value="lastTransaction">
              {t('posLanes.sortLastTransaction')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-xl" />
          ))}
        </div>
      )}

      {lanes.isError && (
        <Empty>{t('posLanes.loadFailed')}</Empty>
      )}

      {lanes.isSuccess && rows.length === 0 && (
        <Empty busy={busy}>
          {all.length === 0
            ? t('posLanes.noLanes')
            : t('posLanes.noneMatch')}
        </Empty>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={busy} />
          <div
            aria-busy={busy}
            className={cn(
              'divide-y rounded-xl border',
              busy && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((lane) => (
              <button
                key={lane.id}
                type="button"
                onClick={() => setEditing(lane)}
                className="hover:bg-accent/60 flex w-full items-start gap-3 p-3 text-left transition-colors"
              >
                <Monitor
                  className={cn(
                    'mt-0.5 size-4 shrink-0',
                    lane.isBlocked ? 'text-destructive' : 'text-muted-foreground'
                  )}
                />

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-medium">
                      {lane.tag || t('posLanes.untagged')}
                    </span>
                    {lane.isBlocked && (
                      <Badge variant="destructive">
                        <Ban className="size-3" />
                        {t('posLanes.blocked')}
                      </Badge>
                    )}
                    {lane.lastReportedVersion ? (
                      <Badge
                        variant={isOutdated(lane) ? 'warning' : 'secondary'}
                      >
                        {lane.lastReportedVersion}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        {t('posLanes.neverReported')}
                      </Badge>
                    )}
                  </div>

                  <p className="text-muted-foreground truncate text-xs">
                    {[
                      lane.lastReportedDeviceManufacturer,
                      lane.lastReportedDeviceModel,
                      lane.lastReportedDeviceName,
                    ]
                      .filter(Boolean)
                      .join(' · ') || t('posLanes.noDevice')}
                  </p>

                  {lane.lastReportedTerminalId && (
                    <p className="text-muted-foreground truncate font-mono text-xs">
                      {lane.lastReportedTerminalId}
                    </p>
                  )}
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-muted-foreground text-xs">
                    {t('posLanes.lastTransaction')}
                  </p>
                  <p className="text-xs font-medium">
                    {lane.lastTransactionOn
                      ? formatDateTime(lane.lastTransactionOn)
                      : '—'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <LaneEditorSheet
        lane={editing}
        businessId={business?.id ?? null}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
  active,
  loading,
  busy,
  onClick,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: 'warn';
  active: boolean;
  /** Nothing to count yet: the figure is a skeleton. */
  loading?: boolean;
  /** A count is on screen and being refreshed: it stays, under a spinner. */
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative rounded-xl border p-3 text-left transition-colors',
        active ? 'border-primary bg-accent/50' : 'hover:bg-accent/40'
      )}
    >
      <BusyOverlay show={busy} size="sm" />
      <div className={cn(busy && 'opacity-60 transition-opacity')}>
        <p className="text-muted-foreground text-xs">{label}</p>
        {loading ? (
          <Skeleton className="mt-1 h-8 w-12" />
        ) : (
          <p
            className={cn(
              'text-2xl font-semibold tabular-nums',
              tone === 'warn' && 'text-destructive'
            )}
          >
            {value}
          </p>
        )}
        {hint && <p className="text-muted-foreground truncate text-xs">{hint}</p>}
      </div>
    </button>
  );
}

function Empty({
  busy,
  children,
}: {
  /** The list is being re-read: it may be about to fill. */
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={busy} />
      <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
        {children}
      </p>
    </div>
  );
}

/**
 * A lane, created or corrected.
 *
 * Creating one takes a tag and nothing else — everything else on the row is
 * reported by the till itself once it starts running, so it is shown here as
 * read-only detail rather than as fields nobody can fill in.
 */
function LaneEditorSheet({
  lane,
  businessId,
  open,
  onOpenChange,
}: {
  lane: PosLane | 'new' | null;
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const create = useCreatePosLane();
  const update = useUpdatePosLane();
  const remove = useDeletePosLane();

  const existing = lane !== 'new' ? lane : null;
  const [tag, setTag] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [hydrated, setHydrated] = useState<string | null>(null);

  const key = existing?.id ?? (lane === 'new' ? 'new' : null);
  if (lane && hydrated !== key) {
    setHydrated(key);
    setTag(existing?.tag ?? '');
    setIsBlocked(existing?.isBlocked ?? false);
  }

  if (!lane) return null;

  const busy = create.isPending || update.isPending || remove.isPending;
  const failed = () => toast.error(t('posLanes.actionFailed'));

  function save() {
    if (existing) {
      update.mutate(
        { id: existing.id, tag, isBlocked },
        {
          onSuccess: () => {
            toast.success(t('posLanes.saved'));
            onOpenChange(false);
          },
          onError: failed,
        }
      );
      return;
    }

    create.mutate(
      { businessId: businessId!, tag },
      {
        onSuccess: () => {
          toast.success(t('posLanes.created'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {existing ? t('posLanes.editLane') : t('posLanes.newLane')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <div className="space-y-1.5">
            <Label>{t('posLanes.tag')}</Label>
            <Input
              value={tag}
              disabled={busy}
              placeholder={t('posLanes.tagPlaceholder')}
              onChange={(event) => setTag(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              {t('posLanes.tagHint')}
            </p>
          </div>

          {existing && (
            <>
              <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                <span className="min-w-0">
                  <span className="block text-sm">
                    {t('posLanes.blocked')}
                  </span>
                  <span className="text-muted-foreground block text-xs">
                    {t('posLanes.blockedHint')}
                  </span>
                </span>
                <Switch
                  checked={isBlocked}
                  disabled={busy}
                  onCheckedChange={setIsBlocked}
                />
              </label>

              {/* Reported by the till, not set here. */}
              <div className="divide-y rounded-xl border">
                <Detail
                  label={t('posLanes.version')}
                  value={existing.lastReportedVersion}
                />
                <Detail
                  label={t('posLanes.device')}
                  value={
                    [
                      existing.lastReportedDeviceManufacturer,
                      existing.lastReportedDeviceModel,
                    ]
                      .filter(Boolean)
                      .join(' ') || null
                  }
                />
                <Detail
                  label={t('posLanes.deviceName')}
                  value={existing.lastReportedDeviceName}
                />
                <Detail
                  label={t('posLanes.terminalId')}
                  value={existing.lastReportedTerminalId}
                  mono
                />
                <Detail
                  label={t('posLanes.lastTransaction')}
                  value={
                    existing.lastTransactionOn
                      ? formatDateTime(existing.lastTransactionOn)
                      : null
                  }
                />
                <Detail
                  label={t('posLanes.createdOn')}
                  value={
                    existing.createdAt
                      ? formatDateTime(existing.createdAt)
                      : null
                  }
                />
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {existing ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={busy}>
                    {remove.isPending && <Loader2 className="animate-spin" />}
                    {t('posLanes.remove')}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('posLanes.remove')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('posLanes.removePrompt', {
                        tag: existing.tag || t('posLanes.untagged'),
                      })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={() =>
                        remove.mutate(existing.id, {
                          onSuccess: () => {
                            toast.success(t('posLanes.removed'));
                            onOpenChange(false);
                          },
                          onError: failed,
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button disabled={busy || tag.trim() === ''} onClick={save}>
                {(create.isPending || update.isPending) && (
                  <Loader2 className="animate-spin" />
                )}
                {existing ? t('common.save') : t('posLanes.create')}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={cn('truncate text-sm', mono && 'font-mono text-xs')}>
        {value || '—'}
      </span>
    </div>
  );
}
